import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import mesApi, {
  LabosNalog,
  LabosScaledRow,
  LabosTrialOption,
  LabosUredaj,
} from "../../../Api/mes.api";
import { Ingredient } from "../../../types/mes";
import NumberInput from "../../../components/DataEntry/NumberInput";
import LabosShell from "./components/LabosShell";
import {
  formatLabosDate,
  NALOG_STATUS_LABEL,
  TARGET_BRASNO_G,
  UREDAJ_LABEL,
} from "./labosUtils";

type PageTab = "plan" | "kreiraj";
type CreateMode = "trial" | "scratch";

interface ScratchRow {
  ingredient_name: string;
  ingredient_type: string;
  baker_percentage: number;
}

const LabosNalozi = () => {
  const [pageTab, setPageTab] = useState<PageTab>("plan");
  const [nalozi, setNalozi] = useState<LabosNalog[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterUredaj, setFilterUredaj] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDays, setFilterDays] = useState(30);

  const [trials, setTrials] = useState<LabosTrialOption[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);

  const [uredaj, setUredaj] = useState<LabosUredaj>("farinograf");
  const [plannedDate, setPlannedDate] = useState(new Date().toISOString().split("T")[0]);
  const [operator, setOperator] = useState("");
  const [notes, setNotes] = useState("");
  const [createMode, setCreateMode] = useState<CreateMode>("trial");
  const [trialId, setTrialId] = useState<number | "">("");
  const [sampleName, setSampleName] = useState("");
  const [scaledPreview, setScaledPreview] = useState<LabosScaledRow[]>([]);
  const [totalMass, setTotalMass] = useState(0);
  const [scaleMeta, setScaleMeta] = useState<{
    water_default_ml?: number;
    starter_water_g?: number;
    water_to_add_g?: number;
    salt_g?: number;
    water_by_method?: boolean;
  }>({});
  const [scratchRows, setScratchRows] = useState<ScratchRow[]>([
    { ingredient_name: "", ingredient_type: "", baker_percentage: 0 },
  ]);
  const [saving, setSaving] = useState(false);

  const loadNalozi = useCallback(async () => {
    setLoading(true);
    try {
      const data = await mesApi.getLabosNalozi({
        days: filterDays,
        status: filterStatus || undefined,
        uredaj: filterUredaj || undefined,
      });
      setNalozi(data);
    } catch (err: any) {
      toast.error(err.message);
      setNalozi([]);
    } finally {
      setLoading(false);
    }
  }, [filterDays, filterStatus, filterUredaj]);

  useEffect(() => {
    if (pageTab === "plan") loadNalozi();
  }, [pageTab, loadNalozi]);

  useEffect(() => {
    mesApi.getLabosTrialsSelect().then(setTrials).catch(() => {});
    mesApi.getIngredients().then(setIngredients).catch(() => {});
  }, []);

  const applyScale = (data: {
    scaled: LabosScaledRow[];
    total_mass_g: number;
    water_default_ml?: number;
    starter_water_g?: number;
    water_to_add_g?: number;
    salt_g?: number;
    water_by_method?: boolean;
  }) => {
    setScaledPreview(data.scaled);
    setTotalMass(data.total_mass_g);
    setScaleMeta({
      water_default_ml: data.water_default_ml,
      starter_water_g: data.starter_water_g,
      water_to_add_g: data.water_to_add_g,
      salt_g: data.salt_g,
      water_by_method: data.water_by_method,
    });
  };

  const clearScale = () => {
    setScaledPreview([]);
    setTotalMass(0);
    setScaleMeta({});
  };

  const runScaleFromTrial = async (tid: number, u: LabosUredaj) => {
    const data = await mesApi.scaleLabosFromTrial(tid, u);
    applyScale(data);
  };

  useEffect(() => {
    if (pageTab !== "kreiraj" || createMode !== "trial" || !trialId) {
      if (createMode === "trial" && !trialId) clearScale();
      return;
    }
    const t = trials.find((x) => x.trial_id === Number(trialId));
    if (t) setSampleName(`${t.trial_code} — ${t.product_name || ""}`);
    runScaleFromTrial(Number(trialId), uredaj).catch((e) => toast.error(e.message));
  }, [trialId, uredaj, createMode, pageTab]);

  const runScratchScale = async (rows: ScratchRow[]) => {
    const valid = rows.filter((r) => r.ingredient_name && r.baker_percentage > 0);
    if (!valid.length) {
      clearScale();
      return;
    }
    const data = await mesApi.previewLabosScale(uredaj, valid);
    applyScale(data);
  };

  // Re-skaliraj scratch recepturu kada se promijeni uređaj.
  useEffect(() => {
    if (pageTab === "kreiraj" && createMode === "scratch") {
      runScratchScale(scratchRows).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uredaj]);

  const updateScratch = (idx: number, field: keyof ScratchRow, value: string | number) => {
    const next = [...scratchRows];
    const row = { ...next[idx], [field]: value };
    if (field === "ingredient_name" && typeof value === "string") {
      const ing = ingredients.find((i) => i.IngredientName === value);
      if (ing) row.ingredient_type = ing.IngredientTypeName || "";
    }
    next[idx] = row;
    setScratchRows(next);
    runScratchScale(next).catch(() => {});
  };

  const handleCreate = async () => {
    if (!scaledPreview.length) {
      toast.error("Nema skaliranih sastojaka");
      return;
    }
    const sn = sampleName.trim();
    if (!sn) {
      toast.error("Unesite naziv uzorka");
      return;
    }
    setSaving(true);
    try {
      const created = await mesApi.createLabosNalog({
        uredaj,
        planned_date: plannedDate,
        sample_name: sn,
        operator: operator || undefined,
        notes: notes || undefined,
        trial_id: createMode === "trial" && trialId ? Number(trialId) : null,
        ingredients: scaledPreview,
      });
      toast.success(`Nalog ${created.nalog_code} kreiran`);
      setPageTab("plan");
      setScaledPreview([]);
      setScratchRows([{ ingredient_name: "", ingredient_type: "", baker_percentage: 0 }]);
      loadNalozi();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (nalogId: number, status: LabosNalog["status"]) => {
    try {
      await mesApi.updateLabosNalogStatus(nalogId, status);
      toast.success("Status ažuriran");
      loadNalozi();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const ingNames = ["", ...ingredients.map((i) => i.IngredientName)];

  return (
    <LabosShell title="MES - Labos nalozi">
      <ToastContainer position="top-center" autoClose={2500} />

      <div className="mes-section">
        <div className="kpi-tabs fq-analysis-tabs">
          <button
            type="button"
            className={`kpi-tab ${pageTab === "plan" ? "active" : ""}`}
            onClick={() => setPageTab("plan")}
          >
            Plan naloga
          </button>
          <button
            type="button"
            className={`kpi-tab ${pageTab === "kreiraj" ? "active" : ""}`}
            onClick={() => setPageTab("kreiraj")}
          >
            Kreiraj nalog
          </button>
        </div>
      </div>

      {pageTab === "plan" && (
        <>
          <div className="mes-section">
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "flex-end" }}>
              <div className="control-group">
                <label>Uređaj</label>
                <select className="select-classic" value={filterUredaj} onChange={(e) => setFilterUredaj(e.target.value)}>
                  <option value="">Svi</option>
                  {(Object.keys(UREDAJ_LABEL) as LabosUredaj[]).map((u) => (
                    <option key={u} value={u}>
                      {UREDAJ_LABEL[u]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="control-group">
                <label>Status</label>
                <select className="select-classic" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="">Svi</option>
                  {(Object.keys(NALOG_STATUS_LABEL) as LabosNalog["status"][]).map((s) => (
                    <option key={s} value={s}>
                      {NALOG_STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="control-group">
                <label>Razdoblje</label>
                <select className="select-classic" value={filterDays} onChange={(e) => setFilterDays(Number(e.target.value))}>
                  {[14, 30, 60, 90].map((d) => (
                    <option key={d} value={d}>
                      Zadnjih {d} dana
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className="btn-secondary btn-sm" onClick={loadNalozi} disabled={loading}>
                Osvježi
              </button>
            </div>
          </div>

          <div className="mes-section">
            {loading && <p>Učitavanje…</p>}
            {!loading && nalozi.length === 0 && <p className="mes-hint">Nema naloga za filter.</p>}
            {nalozi.map((n) => (
              <div key={n.nalog_id} className="labos-nalog-card">
                <div className="labos-nalog-head">
                  <strong>{NALOG_STATUS_LABEL[n.status]}</strong>
                  <span>{UREDAJ_LABEL[n.uredaj]}</span>
                  <span>{formatLabosDate(n.planned_date)}</span>
                  <span>{n.nalog_code}</span>
                  <span>{n.sample_name || "—"}</span>
                </div>
                <div className="labos-nalog-body">
                  <p>
                    Target brašno: <strong>{n.target_brasno_g} g</strong>
                    {n.operator && <> · Operator: {n.operator}</>}
                    {n.trial_code && (
                      <>
                        {" "}
                        · Proba: <strong>{n.trial_code}</strong> {n.trial_product}
                      </>
                    )}
                  </p>
                  {n.notes && <p className="mes-hint">{n.notes}</p>}
                  {n.ingredients && n.ingredients.length > 0 && (
                    <div className="mes-table-wrap">
                      <table className="mes-table mes-table-compact">
                      <thead>
                        <tr>
                          <th>Sirovina</th>
                          <th>Tip</th>
                          <th>Masa (g)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {n.ingredients.map((ing, i) => (
                          <tr key={i}>
                            <td>{ing.ingredient_name}</td>
                            <td>{ing.ingredient_type || "—"}</td>
                            <td>{ing.mass_g}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    {n.status === "plan" && (
                      <>
                        <button type="button" className="btn-sm btn-primary" onClick={() => setStatus(n.nalog_id, "u_tijeku")}>
                          Pokreni
                        </button>
                        <button type="button" className="btn-sm btn-danger" onClick={() => setStatus(n.nalog_id, "otkazano")}>
                          Otkaži
                        </button>
                      </>
                    )}
                    {n.status === "u_tijeku" && (
                      <>
                        <button type="button" className="btn-sm btn-success" onClick={() => setStatus(n.nalog_id, "zavrseno")}>
                          Završi
                        </button>
                        <button type="button" className="btn-sm btn-danger" onClick={() => setStatus(n.nalog_id, "otkazano")}>
                          Otkaži
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {pageTab === "kreiraj" && (
        <div className="mes-section">
          <div className="form-grid-2">
            <div className="control-group">
              <label>Uređaj</label>
              <select
                className="select-classic"
                value={uredaj}
                onChange={(e) => setUredaj(e.target.value as LabosUredaj)}
              >
                {(Object.keys(UREDAJ_LABEL) as LabosUredaj[]).map((u) => (
                  <option key={u} value={u}>
                    {UREDAJ_LABEL[u]}
                  </option>
                ))}
              </select>
            </div>
            <div className="control-group">
              <label>Datum mjerenja</label>
              <input type="date" className="input-classic" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} />
            </div>
            <div className="control-group">
              <label>Operator</label>
              <input className="input-classic" value={operator} onChange={(e) => setOperator(e.target.value)} />
            </div>
          </div>
          <p className="mes-hint">Target masa brašna: {TARGET_BRASNO_G[uredaj]} g</p>
          {uredaj === "amilograf" && (
            <p className="mes-hint">
              Uključuju se samo brašno-sastojci (uklj. brašno iz startera i poluproizvoda). Voda je fiksno{" "}
              {scaleMeta.water_default_ml ?? 450} mL, umanjeno za vodu iz startera.
            </p>
          )}
          {uredaj === "ekstenzograf" && (
            <p className="mes-hint">Dodaje se 2% soli na masu brašna ({(TARGET_BRASNO_G[uredaj] * 2) / 100} g).</p>
          )}
          {uredaj === "farinograf" && (
            <p className="mes-hint">QC uzorci brašna imaju prioritet. Količina vode ovisi o odabranoj metodi mjerenja.</p>
          )}

          <div style={{ margin: "1rem 0" }}>
            <label style={{ marginRight: 12 }}>
              <input
                type="radio"
                checked={createMode === "trial"}
                onChange={() => setCreateMode("trial")}
              />{" "}
              Iz probe
            </label>
            <label>
              <input
                type="radio"
                checked={createMode === "scratch"}
                onChange={() => {
                  setCreateMode("scratch");
                  clearScale();
                }}
              />{" "}
              Iz nule
            </label>
          </div>

          {createMode === "trial" && (
            <div className="control-group" style={{ maxWidth: 480 }}>
              <label>Proba</label>
              <select
                className="select-classic"
                value={trialId}
                onChange={(e) => setTrialId(e.target.value ? Number(e.target.value) : "")}
              >
                <option value="">— odaberi —</option>
                {trials.map((t) => (
                  <option key={t.trial_id} value={t.trial_id}>
                    {t.trial_code} — {formatLabosDate(t.trial_date)} — {t.product_name || "—"}
                  </option>
                ))}
              </select>
            </div>
          )}

          {createMode === "scratch" && (
            <>
              <div className="mes-table-wrap">
                <table className="mes-table">
                <thead>
                  <tr>
                    <th>Sirovina</th>
                    <th>Tip</th>
                    <th>PEK %</th>
                  </tr>
                </thead>
                <tbody>
                  {scratchRows.map((row, idx) => (
                    <tr key={idx}>
                      <td>
                        <select
                          className="select-classic"
                          value={row.ingredient_name}
                          onChange={(e) => updateScratch(idx, "ingredient_name", e.target.value)}
                        >
                          {ingNames.map((name) => (
                            <option key={name || "empty"} value={name}>
                              {name || "— odaberi —"}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>{row.ingredient_type || "—"}</td>
                      <td>
                        <NumberInput
                          className="input-classic"
                          step="0.1"
                          value={row.baker_percentage}
                          onValueChange={(v) => updateScratch(idx, "baker_percentage", v)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() =>
                    setScratchRows([
                      ...scratchRows,
                      { ingredient_name: "", ingredient_type: "", baker_percentage: 0 },
                    ])
                  }
                >
                  + Sirovina
                </button>
                {scratchRows.length > 1 && (
                  <button type="button" className="btn-secondary btn-sm" onClick={() => setScratchRows(scratchRows.slice(0, -1))}>
                    Ukloni zadnju
                  </button>
                )}
              </div>
              <div className="control-group" style={{ marginTop: 12, maxWidth: 400 }}>
                <label>Naziv uzorka</label>
                <input className="input-classic" value={sampleName} onChange={(e) => setSampleName(e.target.value)} />
              </div>
            </>
          )}

          {scaledPreview.length > 0 && (
            <>
              <h4 style={{ marginTop: "1.5rem" }}>Receptura skalirana ({TARGET_BRASNO_G[uredaj]} g brašna)</h4>
              <div className="mes-table-wrap">
                <table className="mes-table mes-table-compact">
                <thead>
                  <tr>
                    <th>Sirovina</th>
                    <th>Tip</th>
                    <th>Masa (g)</th>
                  </tr>
                </thead>
                <tbody>
                  {scaledPreview.map((r, i) => (
                    <tr key={i} style={r.is_manual_water ? { fontWeight: 600 } : undefined}>
                      <td>
                        {r.ingredient_name}
                        {r.is_manual_water ? " 💧" : ""}
                      </td>
                      <td>{r.ingredient_type || "—"}</td>
                      <td>{r.mass_g}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <p className="mes-hint">Ukupna masa zamjesa: {totalMass.toFixed(1)} g</p>
              {uredaj === "amilograf" && scaleMeta.water_to_add_g != null && (
                <p className="mes-hint">
                  Voda: {scaleMeta.water_default_ml} mL − {scaleMeta.starter_water_g ?? 0} g (starter) ={" "}
                  <strong>{scaleMeta.water_to_add_g} mL za ručno dodati</strong>
                </p>
              )}
              {uredaj === "ekstenzograf" && scaleMeta.salt_g != null && (
                <p className="mes-hint">Dodana sol: <strong>{scaleMeta.salt_g} g</strong> (2% brašna)</p>
              )}
              {uredaj === "farinograf" && scaleMeta.water_by_method && (
                <p className="mes-hint">Voda se dodaje prema metodi mjerenja (definira operater na uređaju).</p>
              )}
            </>
          )}

          <div className="control-group" style={{ marginTop: 12 }}>
            <label>Napomena</label>
            <input className="input-classic" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <button type="button" className="btn-primary" style={{ marginTop: 16 }} onClick={handleCreate} disabled={saving}>
            {saving ? "Spremanje…" : "Kreiraj nalog"}
          </button>
        </div>
      )}
    </LabosShell>
  );
};

export default LabosNalozi;
